import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Code2, ShieldCheck, Headset, Phone, Navigation } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useBranding } from "@/context/BrandingContext";
import { openCookiePreferences, useCookieBannerEnabled } from "@/lib/consent";
import { trackEvent } from "@/lib/track";
import api from "@/lib/api";
import { useTemplateSections, findEnabledSection } from "@/lib/useTemplateSections";
import { ICON_COMPONENTS } from "@/lib/iconChoices";
import RichText from "@/components/RichText";

// Colonne d'origine, codée en dur — repli tant que la section "Liens du
// pied de page" (Sprint 11, Theme Studio) n'a jamais été configurée pour
// cette boutique.
const DEFAULT_FOOTER_HEADING = "Nos offres";
const DEFAULT_FOOTER_DESCRIPTION = "Agence de développement d'applications web & mobiles sur mesure, du MVP à la plateforme SaaS.";
const DEFAULT_FOOTER_LINKS = [
  { label: "Applications mobiles", url: "/categorie/applications-mobiles" },
  { label: "Applications web", url: "/categorie/applications-web" },
  { label: "E-commerce", url: "/categorie/e-commerce" },
  { label: "Maintenance & support", url: "/categorie/maintenance-support" },
];
// Bandeau de réassurance d'origine — repli tant que la section "Bandeau de
// confiance" (footer_trust_bar) n'a jamais été configurée pour cette
// boutique. Mêmes 3 icônes que le JSX codé en dur ci-dessous.
const DEFAULT_TRUST_ITEMS = [
  { icon: "Code2", title: "Code livré, code à vous", text: "Aucune dépendance à notre agence après livraison." },
  { icon: "ShieldCheck", title: "Devis gratuit sous 48h", text: "Sans engagement, réponse rapide et transparente." },
  { icon: "Headset", title: "Support post-lancement", text: "Accompagnement inclus après la mise en ligne." },
];
const DEFAULT_TRUST_ICONS = { Code2, ShieldCheck, Headset };
// Colonne "Compte" d'origine, codée en dur — repli tant que la section
// "Colonne Compte" (footer_account_links) n'a jamais été configurée.
const DEFAULT_ACCOUNT_HEADING = "Compte";
const DEFAULT_ACCOUNT_LINKS = [
  { label: "Se connecter", url: "/connexion" },
  { label: "Mes commandes", url: "/compte" },
  { label: "Toutes nos offres", url: "/boutique" },
];

const mapsUrl = (store) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${store.address}, ${store.postal_code || ""} ${store.city}, ${store.country || ""}`
  )}`;

const StoreCard = ({ store }) => (
  <div data-testid={`footer-store-${store.id}`} className="text-sm text-footer-text/70 leading-relaxed">
    <p className="text-footer-text/90 font-medium">{store.name}</p>
    <p className="mt-1">{store.address}, {store.postal_code} {store.city}</p>
    <div className="flex items-center gap-4 mt-2">
      <a
        href={`tel:${store.phone.replace(/\s+/g, "")}`}
        onClick={() => trackEvent("store_call_click", { store_id: store.id, store_name: store.name })}
        className="flex items-center gap-1.5 text-gold hover:text-footer-text transition-colors"
      >
        <Phone className="h-3.5 w-3.5" /> Appeler
      </a>
      <a
        href={mapsUrl(store)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackEvent("store_directions_click", { store_id: store.id, store_name: store.name })}
        className="flex items-center gap-1.5 text-gold hover:text-footer-text transition-colors"
      >
        <Navigation className="h-3.5 w-3.5" /> Itinéraire
      </a>
    </div>
  </div>
);

export const Footer = () => {
  const bannerEnabled = useCookieBannerEnabled();
  const branding = useBranding();
  const [stores, setStores] = useState([]);
  const [footerSections, footerLoaded] = useTemplateSections("footer");
  // Pendant le chargement (!footerLoaded) : rien n'est affiché pour éviter
  // le flash du contenu agence par défaut. Une fois chargé, on distingue :
  //  - footerSections === null → API en échec → repli sur les défauts
  //  - type absent du tableau → jamais configuré → repli sur les défauts
  //  - type présent + désactivé/vide → choix délibéré → on le respecte
  const neverTouched = (type) => footerLoaded && (footerSections === null || !footerSections.some((s) => s.type === type));

  const linksSection = findEnabledSection(footerSections, "footer_links");
  // Masque le pied de page ENTIER — ne peut être lu que sur une section
  // "footer_links" active (voir hide_entire_footer, theme_service.py) ;
  // jamais vrai avant le premier chargement ni si le thème n'y a jamais
  // touché (repli sur le pied de page complet, comme aujourd'hui).
  const hideEntireFooter = linksSection?.props?.hide_entire_footer === "true";
  const hideLogoBlock = linksSection?.props?.hide_logo_block === "true";
  // "footer_links" est dans TEMPLATE_DEFAULT_ORDER : toujours présent une fois
  // chargé si jamais configuré — son absence post-chargement = suppression.
  // Pendant le chargement (!footerLoaded) on affiche rien (pas de flash
  // agence). En cas d'échec API (footerLoaded + footerSections===null) on
  // bascule sur les défauts pour garder la page lisible.
  const linksNeverTouched = footerLoaded && footerSections === null;
  const footerHeading = linksNeverTouched ? DEFAULT_FOOTER_HEADING : (linksSection?.props?.heading || "");
  const footerDescription = linksNeverTouched ? DEFAULT_FOOTER_DESCRIPTION : (linksSection?.props?.description || "");
  const footerLinks = linksNeverTouched
    ? DEFAULT_FOOTER_LINKS
    : linksSection
      ? (linksSection.blocks || [])
          .filter((b) => b.enabled !== false && b.props?.label && b.props?.url)
          .map((b) => ({ label: b.props.label, url: b.props.url }))
      : [];

  const trustSection = findEnabledSection(footerSections, "footer_trust_bar");
  const trustItems = neverTouched("footer_trust_bar")
    ? DEFAULT_TRUST_ITEMS.map((it) => ({ icon: DEFAULT_TRUST_ICONS[it.icon], title: it.title, text: it.text }))
    : trustSection
      ? (trustSection.blocks || [])
          .filter((b) => b.enabled !== false && b.props?.title)
          .map((b) => ({ icon: ICON_COMPONENTS[b.props.icon] || ShieldCheck, title: b.props.title, text: b.props.text }))
      : [];

  const accountSection = findEnabledSection(footerSections, "footer_account_links");
  const accountNeverTouched = neverTouched("footer_account_links");
  const accountHeading = accountNeverTouched ? DEFAULT_ACCOUNT_HEADING : (accountSection?.props?.heading || "");
  const accountLinks = accountNeverTouched
    ? DEFAULT_ACCOUNT_LINKS
    : accountSection
      ? (accountSection.blocks || [])
          .filter((b) => b.enabled !== false && b.props?.label && b.props?.url)
          .map((b) => ({ label: b.props.label, url: b.props.url }))
      : [];

  useEffect(() => {
    // Toujours ramener un tableau : la colonne teste désormais `stores.length`
    // et une réponse inattendue ferait planter tout le pied de page (piège
    // « .map is not a function » documenté dans DEPLOYMENT.md §12).
    api.get("/stores")
      .then(({ data }) => setStores(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  if (!footerLoaded) return null;
  if (hideEntireFooter) return null;

  return (
  <footer data-testid="site-footer" className="bg-footer-bg text-footer-text mt-24 md:mt-32 animate-fade-in">
    {trustItems.length > 0 && (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-8 px-6 md:px-12 py-10 border-b border-white/10">
        {trustItems.map((item, i) => (
          <div key={i} className="flex items-start gap-3">
            <item.icon className="h-6 w-6 text-gold shrink-0" strokeWidth={1.4} />
            <div>
              <RichText as="p" className="font-sans text-sm font-semibold" html={item.title} />
              {item.text && <p className="text-xs text-footer-text/60 mt-1">{item.text}</p>}
            </div>
          </div>
        ))}
      </div>
    )}

    <div className="grid grid-cols-2 md:grid-cols-4 gap-10 px-6 md:px-12 py-14">
      {/* min-w-0 : une colonne de grille CSS résiste par défaut à toute
          largeur inférieure au contenu non enveloppé qu'elle contient — sans
          ça, un logo avec un nom/tagline long débordait sur la colonne
          "Nos offres" voisine au lieu de rester dans sa propre colonne. */}
      {!hideLogoBlock && (
        <div className="col-span-2 md:col-span-1 min-w-0">
          <Logo to={null} size="lg" variant="light" testId="footer-logo" />
          {footerDescription && (
            <p className="text-sm text-footer-text/60 mt-5 max-w-xs leading-relaxed">
              {footerDescription}
            </p>
          )}
        </div>
      )}
      {footerLinks.length > 0 && (
        <div>
          {footerHeading && <RichText as="p" className="text-xs uppercase tracking-[0.2em] text-footer-text/40 mb-4" html={footerHeading} />}
          <ul className="space-y-3 text-sm text-footer-text/80">
            {footerLinks.map((l) => (
              <li key={l.url}>
                {l.url.includes("#") ? (
                  <a href={l.url} className="hover:text-gold transition-colors">{l.label}</a>
                ) : (
                  <Link to={l.url} className="hover:text-gold transition-colors">{l.label}</Link>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {accountLinks.length > 0 && (
        <div>
          {accountHeading && <RichText as="p" className="text-xs uppercase tracking-[0.2em] text-footer-text/40 mb-4" html={accountHeading} />}
          <ul className="space-y-3 text-sm text-footer-text/80">
            {accountLinks.map((l) => (
              <li key={l.url}>
                {l.url.includes("#") ? (
                  <a href={l.url} className="hover:text-gold transition-colors">{l.label}</a>
                ) : (
                  <Link to={l.url} className="hover:text-gold transition-colors">{l.label}</Link>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {/* Colonne masquée en l'absence de magasin : un titre « Nous
          rencontrer » suivi de rien n'annonce rien, et laissait un trou dans
          la grille. Même règle que les colonnes de liens voisines, qui se
          masquent déjà quand elles sont vides. */}
      {stores.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-footer-text/40 mb-4">Nous rencontrer</p>
          <div className="space-y-4">
            {stores.map((s) => <StoreCard key={s.id} store={s} />)}
          </div>
        </div>
      )}
    </div>
    <div className="px-6 md:px-12 py-6 border-t border-white/10 flex flex-col sm:flex-row gap-3 sm:gap-6 sm:items-center text-xs text-footer-text/40">
      <p>© {new Date().getFullYear()}{branding.loaded && ` ${branding.name}`}. Tous droits réservés.</p>
      <Link to="/mentions-legales" className="hover:text-gold transition-colors">
        Mentions légales
      </Link>
      <Link to="/confidentialite" className="hover:text-gold transition-colors">
        Politique de confidentialité
      </Link>
      {bannerEnabled && (
        <button onClick={openCookiePreferences} className="hover:text-gold transition-colors text-left">
          Gérer les cookies
        </button>
      )}
    </div>
  </footer>
  );
};
